import { useContext, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"
import { SplitButton } from "primereact/splitbutton"
import { NOTES_PARTICIPANT } from "./queries"
import { UserContext } from "../../context/userContext"
import LoaderFullPage from "../../components/Loaders"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import GLOBALS_STATUS from "../../config/globalsStatus"
import { Button } from "primereact/button"
import parseJsonData from "../../helpers/parseJsonData"
import isParticipantActive from "../../helpers/isParticipantActive"
import { parseDate } from "../../helpers/dateNormalization"

import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"

import styles from "../../styles/Page-Engagements.module.css"
import { Calendar } from "primereact/calendar"

import { formatDate } from "../../components/DataTable/helpers"
import { handleOptsForFilter } from "../../components/DataTable/helpers"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function NotesManagement() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [dateRange, setDateRange] = useState(null)

  const [unassignedKioskMember, setUnassignedKioskMember] = useState(false)
  const [hasActiveServiceEpisode, setHasActiveServiceEpisode] = useState(false)

  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const ID_TEMPLATE = "note_form"
  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  useQuery(NOTES_PARTICIPANT, {
    variables: {
      participant_id: userCtx.participant_id,
      organization_id: userCtx.session.user.organization_id,
      idTemplate: ID_TEMPLATE,
      staff_id: userCtx.session.user.staff_id,
      participant_membership_id: userCtx.participant_membership_id,
    },
    fetchPolicy: "no-cache",
    skip: !userCtx.session.user.staff_id || !userCtx.participant_id,
    onCompleted: async (data) => {
      try {
        setUnassignedKioskMember(
          data.participant.staff_full_name === "Unassigned Kiosk Member"
        )
        setHasActiveServiceEpisode(!!data.serviceEpisode)

        if (data.serviceEpisode) {
          setHasActiveServiceEpisode(true)
        }

        if (!data || !data.engagementsParticipant) {
          throw new Error("Data is missing.")
        }

        const dataFormatted = parseJsonData(data.engagementsParticipant.data)
        const formattedNotes = dataFormatted.map((note) => {
          const isEditable = userCtx.session.user.staff_role <= 6
          const commonProps = {
            label: "View",
            onClick: () => {
              userCtx.changeParticipant(note.participant_id)
              navigateWithOrigin(
                navigate,
                `/form/view/${note.idTemplate}/${note.idStamp}`
              )
            },
          }
          const handleLocations = (locationIds) => {
            return locationIds
              ? locationIds.map((location) => location.label).join(", ")
              : "No Locations"
          }
          const actionButton =
            isEditable &&
            isParticipantActive(userCtx.participant_membership_status) ? (
              <SplitButton
                {...commonProps}
                model={[
                  {
                    label: "Edit",
                    command: () => {
                      navigate(`/form/${note.idTemplate}/1/${note.idStamp}`)
                    },
                  },
                ]}
              />
            ) : (
              <Button {...commonProps} />
            )

          return {
            // IMPORTANT: The Date Range filter requires the `date` to be a Date object.
            // Use `parseDate` because `note.form_date` is provided as a string.
            date: parseDate(note.form_date),
            time: new Date(
              note.date_created._seconds * 1000
            ).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
            locations: handleLocations(note.form__location_ids),
            staff: note.staff_full_name,
            note: note.note.replace(/<[^>]+>/g, ""),
            actions: actionButton,
          }
        })

        setData(formattedNotes)
        setLoading(false)
      } catch (error) {
        setErrorMessage(error.message)
        setLoading(false)
      }
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setLoading(false)
    },
  })
  if (loading) return <LoaderFullPage />
  const uniqueFacilitatingStaff = handleOptsForFilter(data, "staff")

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },

      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Staff",
      value: filters.staff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("staff", e.value),
      onClear: () => onFilterChange("staff", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "time", header: "Time" },
    { field: "locations", header: "Locations" },
    { field: "staff", header: "Staff", sortable: true },
    { field: "note", header: "Note" },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => rowData.actions || <span>No Actions Available</span>,
    },
  ]

  const isKioskMember =
    userCtx.participant_membership_status === GLOBALS_STATUS.KIOSK_MEMBER &&
    unassignedKioskMember

  const isActiveStatus = _.includes(
    GLOBALS_STATUS.ACTIVE_STATUS,
    userCtx.participant_membership_status
  )

  const shouldShowAddNote =
    isActiveStatus || (isKioskMember && hasActiveServiceEpisode)

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <div>
          <h1 className={styles.title}>Participant notes</h1>
          {shouldShowAddNote ? (
            <Link to="/form/note_form/1" className="btn">
              Add Note
            </Link>
          ) : null}
          <br />
          <br />
          <GenericTable
            data={data}
            columns={columns}
            filters={filters}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            header={
              <FilterHeader
                filters={filterConfig}
                globalFilterValue={globalFilterValue}
                onGlobalFilterChange={onGlobalFilterChange}
                clearGlobalFilter={clearGlobalFilter}
                clearAllFilters={handleClearAllFilters}
              />
            }
            paginator
            rows={50}
          />
        </div>
      </div>
    </>
  )
}
